import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { translations } from '../i18n/translations';

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = {
  en: { code: 'en', name: 'English', flag: '🇬🇧' },
  sl: { code: 'sl', name: 'Slovenščina', flag: '🇸🇮' },
  de: { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
};

const DEFAULT_LANGUAGE = 'en';

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [loading, setLoading] = useState(true);

  // Load language preference on mount
  useEffect(() => {
    loadLanguagePreference();
  }, [user, isAuthenticated]);

  const loadLanguagePreference = async () => {
    try {
      if (isAuthenticated && user) {
        // Try to load from database for authenticated users
        const { data, error } = await supabase
          .from('profiles')
          .select('language_preference')
          .eq('id', user.id)
          .single();

        if (!error && data?.language_preference) {
          setLanguage(data.language_preference);
          localStorage.setItem('language', data.language_preference);
          document.documentElement.setAttribute('lang', data.language_preference);
        } else {
          // Fallback to localStorage
          const savedLang = localStorage.getItem('language') || DEFAULT_LANGUAGE;
          setLanguage(savedLang);
          document.documentElement.setAttribute('lang', savedLang);
        }
      } else {
        // For non-authenticated users, use localStorage
        const savedLang = localStorage.getItem('language') || DEFAULT_LANGUAGE;
        setLanguage(savedLang);
        document.documentElement.setAttribute('lang', savedLang);
      }
    } catch (error) {
      console.error('Error loading language preference:', error);
      const savedLang = localStorage.getItem('language') || DEFAULT_LANGUAGE;
      setLanguage(savedLang);
      document.documentElement.setAttribute('lang', savedLang);
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = async (newLanguage) => {
    if (!SUPPORTED_LANGUAGES[newLanguage]) {
      console.error('Unsupported language:', newLanguage);
      return;
    }

    try {
      setLanguage(newLanguage);
      localStorage.setItem('language', newLanguage);
      document.documentElement.setAttribute('lang', newLanguage);

      // Save to database if user is authenticated
      if (isAuthenticated && user) {
        const { error } = await supabase
          .from('profiles')
          .update({ language_preference: newLanguage })
          .eq('id', user.id);

        if (error) {
          console.error('Error saving language to database:', error);
        }
      }
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  // Get translated text helper
  const t = (path) => {
    const keys = path.split('.');
    let value = translations[language];
    
    for (const key of keys) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        // Fallback to English if translation not found
        let fallback = translations[DEFAULT_LANGUAGE];
        for (const k of keys) {
          if (fallback && typeof fallback === 'object') {
            fallback = fallback[k];
          } else {
            return path; // Return path if neither translation found
          }
        }
        return fallback;
      }
    }
    
    return value || path;
  };

  const value = {
    currentLanguage: language,
    language,
    changeLanguage,
    t,
    loading,
    availableLanguages: Object.values(SUPPORTED_LANGUAGES),
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
